/**
 * FileAssetPickerModal — the "Assets → Files" window opened by the attach-file
 * button in the agent composer. Shows the user's existing uploaded files (their
 * private library over the shared HeyGen account, served by the v2-assets
 * lambda) and lets them upload a new one from their device. Selecting files
 * attaches them to the chat message as HeyGen asset_ids.
 *
 * Visual language is intentionally identical to the avatar/voice/brand pickers
 * (same shell + shared PickRow) so all the agent menus feel like one product.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X, Upload, Loader2, Image as ImageIcon, Film, Music, FileText, Trash2 } from 'lucide-react';
import { cn, assetFileError, SUPPORTED_ASSET_TYPES, SUPPORTED_ASSET_TYPES_WITH_AUDIO } from '@/v2/utils';
import { v2Service } from '@/api/v2Service';
import type { V2Asset } from '@/api/v2Types';
import { PickRow, pickList } from '@/v2/components/AssetPickerModal';
import { toast } from '@/v2/toast';
import { trackAmpAssetUploaded, assetSizeBucket } from '@/services/amplitudeService';

interface Props {
  userId: string;
  /** asset_ids already attached to the message — shown pre-selected. */
  attachedIds?: string[];
  onClose: () => void;
  onAttach: (items: Array<{ assetId: string; name: string; type?: string; url?: string | null }>) => void;
  /** Lists and uploads audio too (home composer: LongVid uses it; HeyGen generate drops it). */
  allowAudio?: boolean;
}

function typeIcon(t?: string | null) {
  const v = (t || '').toLowerCase();
  if (v.includes('image')) return ImageIcon;
  if (v.includes('video')) return Film;
  if (v.includes('audio')) return Music;
  return FileText;
}

function prettySize(bytes?: number) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileAssetPickerModal({ userId, attachedIds = [], onClose, onAttach, allowAudio = false }: Props) {
  const { t } = useTranslation('v2');
  const [assets, setAssets] = useState<V2Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(attachedIds));
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // asset_refs uploaded this session to auto-select once they finish processing.
  const pendingSelectRef = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await v2Service.listAssets(userId);
      // HeyGen's Video Agent rejects audio, so it is hidden unless allowAudio opts in.
      const list = (res.assets || []).filter((a) => a.status !== 'deleted' && (allowAudio || a.asset_type !== 'audio'));
      setAssets(list);
      // A file uploaded this session may have just finished processing — auto-select it
      // (the user uploaded it to attach it) now that it's genuinely ready to render.
      if (pendingSelectRef.current.size) {
        setSelected((prev) => {
          const next = new Set(prev);
          for (const a of list) {
            if (a.status === 'ready' && pendingSelectRef.current.has(a.asset_ref)) {
              next.add(a.asset_id);
              pendingSelectRef.current.delete(a.asset_ref);
            }
          }
          return next;
        });
      }
    } catch {
      setErr(t('fileAssetPicker.loadError'));
    } finally { setLoading(false); }
  }, [userId, allowAudio]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    void load();
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose, load]);

  // While any file is still processing on HeyGen, poll so it flips to 'ready' (and
  // auto-selects if it was just uploaded). Pauses on a hidden tab; stops when idle.
  const anyProcessing = assets.some((a) => a.status === 'processing');
  useEffect(() => {
    if (!anyProcessing) return;
    const id = setInterval(() => { if (!document.hidden) void load(); }, 4000);
    return () => clearInterval(id);
  }, [anyProcessing, load]);

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const uploadFromDevice = async (files: FileList | null) => {
    if (!files || !files.length) return;
    for (const file of Array.from(files).slice(0, 20)) {
      const bad = assetFileError(file, { allowAudio });
      if (bad) {
        toast.error(bad.reason === 'unsupported'
          ? t('fileAssetPicker.unsupportedType', { name: file.name, types: allowAudio ? SUPPORTED_ASSET_TYPES_WITH_AUDIO : SUPPORTED_ASSET_TYPES })
          : t('fileAssetPicker.fileTooLarge', { name: file.name, max: bad.maxMb }));
        continue;
      }
      try {
        setUploadPct(1);
        const asset = await v2Service.uploadAsset(userId, file, (p) => setUploadPct(p));
        trackAmpAssetUploaded({ source: 'file_picker', fileType: asset.asset_type || file.type, sizeBucket: assetSizeBucket(file.size) });
        setAssets((prev) => [asset, ...prev.filter((a) => a.asset_ref !== asset.asset_ref)]);
        // Only attach it once it's genuinely ready; if HeyGen is still processing, remember
        // it and the poll above will auto-select it the moment it becomes ready.
        if (asset.status === 'ready') setSelected((prev) => new Set(prev).add(asset.asset_id));
        else pendingSelectRef.current.add(asset.asset_ref);
      } catch {
        toast.error(t('fileAssetPicker.uploadError', { name: file.name }));
      } finally {
        setUploadPct(null);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const remove = async (a: V2Asset) => {
    setAssets((prev) => prev.filter((x) => x.asset_ref !== a.asset_ref));
    setSelected((prev) => { const n = new Set(prev); n.delete(a.asset_id); return n; });
    try { await v2Service.deleteAsset(userId, a.asset_ref); }
    catch { toast.error(t('fileAssetPicker.deleteError')); void load(); }
  };

  const confirm = () => {
    const items = assets.filter((a) => selected.has(a.asset_id)).map((a) => ({ assetId: a.asset_id, name: a.name, type: a.asset_type ?? undefined, url: a.url }));
    onAttach(items);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div className="w-full max-w-3xl h-[80vh] flex flex-col rounded-3xl bg-[#15151d] border border-white/10 shadow-2xl animate-slide-up overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="shrink-0 px-6 sm:px-8 pt-6 pb-4 flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-text-primary tracking-tight">{t('fileAssetPicker.title')}</h2>
            <p className="text-sm text-text-tertiary mt-0.5">{t('fileAssetPicker.subtitle')}</p>
          </div>
          <button onClick={onClose} className="shrink-0 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Upload action — mirrors the create-action row in the other menus */}
        <div className="shrink-0 px-6 sm:px-8 pb-4">
          <input ref={fileRef} type="file" multiple accept={allowAudio ? 'image/*,video/*,audio/*,application/pdf' : 'image/*,video/*,application/pdf'} className="hidden" onChange={(e) => void uploadFromDevice(e.target.files)} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploadPct !== null}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-semibold text-text-primary hover:bg-white/10 hover:border-primary/30 transition-colors disabled:opacity-60"
          >
            {uploadPct !== null
              ? <><Loader2 className="w-4 h-4 animate-spin text-primary" /> {t('fileAssetPicker.uploading', { pct: uploadPct })}</>
              : <><Upload className="w-4 h-4 text-primary" /> {t('fileAssetPicker.uploadFromDevice')}</>}
          </button>
        </div>

        {/* Library */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-2">
          {err && <p className="text-xs text-amber-300/80 mb-4">{err}</p>}
          {loading ? (
            <div className="flex justify-center py-12 text-text-tertiary"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : assets.length ? (
            <div className={pickList}>
              {assets.map((a) => {
                const ready = a.status === 'ready';
                return (
                <PickRow
                  key={a.asset_ref}
                  name={a.name}
                  img={a.asset_type?.includes('image') ? a.url ?? undefined : undefined}
                  icon={typeIcon(a.asset_type)}
                  sub={[a.asset_type?.replace(/^.*\//, '') || t('fileAssetPicker.fileFallback'), prettySize(a.size_bytes)].filter(Boolean).join(' · ')}
                  selected={selected.has(a.asset_id)}
                  disabled={!ready}
                  badge={!ready ? t('assets.processing') : undefined}
                  onClick={() => toggle(a.asset_id)}
                  trailing={
                    <button onClick={(e) => { e.stopPropagation(); void remove(a); }} className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-text-tertiary hover:text-error shrink-0" title={t('fileAssetPicker.deleteFile')}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  }
                />
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-text-tertiary py-12 text-center">{t('fileAssetPicker.empty')}</p>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 sm:px-8 py-4 border-t border-white/10 flex items-center justify-between">
          <span className="text-xs text-text-tertiary">{selected.size ? t('fileAssetPicker.countSelected', { count: selected.size }) : t('fileAssetPicker.selectPrompt')}</span>
          <button
            onClick={confirm}
            disabled={!selected.size || uploadPct !== null}
            className={cn(
              'px-5 py-2.5 rounded-full text-sm font-semibold transition-all',
              selected.size && uploadPct === null ? 'bg-gradient-to-r from-primary to-accent text-white hover:opacity-95 hover:shadow-glow' : 'bg-white/5 text-text-tertiary cursor-not-allowed'
            )}
          >
            {selected.size ? t('fileAssetPicker.attachCount', { count: selected.size }) : t('fileAssetPicker.attach')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
